import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import toast from "react-hot-toast";

import { rsvpApi } from "../../api/rsvp";
import { APP_WORKSPACE } from "../../constants/appWorkspace";
import { queryKeys } from "../../queryKeys";
import type { EventPromoter } from "../../types/rsvp";
import "./ticketTemplate.css";

type Props = {
  eventId: string;
  token?: string;
};

export const EventPromoterPanel: React.FC<Props> = ({ eventId, token }) => {
  const workspace = APP_WORKSPACE;
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [expanded, setExpanded] = useState(true);

  const promotersQuery = useQuery({
    queryKey: queryKeys.eventPromoters(workspace, eventId),
    queryFn: () => rsvpApi.listPromoters(eventId, token),
    enabled: Boolean(eventId),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.eventPromoters(workspace, eventId) });
  };

  const createMutation = useMutation({
    mutationFn: () => rsvpApi.createPromoter(eventId, name, token),
    onSuccess: async () => {
      toast.success("Promoter added");
      setName("");
      await invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not add promoter"),
  });

  const deleteMutation = useMutation({
    mutationFn: (promoterId: string) => rsvpApi.deletePromoter(eventId, promoterId, token),
    onSuccess: async () => {
      toast.success("Promoter removed");
      await invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Delete failed"),
  });

  const rows: EventPromoter[] = promotersQuery.data ?? [];

  return (
    <section className="event-promoter-panel" aria-label="Manage promoters">
      <div className="event-promoter-panel__head-row">
        <div>
          <h2 className="event-rsvp__manual-title">Manage Promoters</h2>
          <p className="eventos-subtle event-rsvp__manual-hint">
            Add promoters and track how many guests each one pulls.
          </p>
        </div>
        <button
          type="button"
          className="eventos-nav-btn"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Collapse" : "Expand"}
        </button>
      </div>

      {expanded ? (
        <>
          <form
            className="event-promoter-panel__head"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim()) return;
              createMutation.mutate();
            }}
          >
            <label className="event-promoter-panel__add eventos-field">
              <span className="eventos-field-label">Promoter name</span>
              <input
                className="eventos-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Promoter name"
              />
            </label>
            <button
              type="submit"
              className="eventos-primary-btn event-promoter-panel__add-btn"
              disabled={createMutation.isPending || !name.trim()}
            >
              Add
            </button>
          </form>

          <ul className="event-promoter-panel__list">
            {promotersQuery.isPending ? (
              <li className="eventos-subtle">Loading promoters…</li>
            ) : rows.length === 0 ? (
              <li className="eventos-subtle">No promoters yet.</li>
            ) : (
              rows.map((row) => (
                <li key={row.id} className="event-promoter-panel__item">
                  <span>
                    <strong>{row.name}</strong>: {row.guest_count ?? 0}
                  </span>
                  <button
                    type="button"
                    className="eventos-nav-btn eventos-nav-btn--danger event-promoter-panel__remove"
                    onClick={() => {
                      if (window.confirm(`Remove ${row.name}?`)) deleteMutation.mutate(row.id);
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))
            )}
          </ul>
        </>
      ) : null}
    </section>
  );
};
